import { lazy, Suspense, useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { parseNumber } from "./numberInput";
import { bmiFromQuery, bmiToQuery, bmiOf, roundBmi, classify, healthyRangeKg, changeToHealthy, ageStatus, waistToHeight, gaugePos, gaugeSegments, cutsFor, type Category } from "./bmi";
import { ToolContainer } from "@/components/ToolLayout";
import { MeasureField, UnitToggle, type Units } from "./MeasureField";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogToolUsage } from "@/hooks/useLogToolUsage";

// BMI Prime, Ponderal Index, the WHO table, target weight, working and export load on demand.
const BmiExtras = lazy(() => import("./BmiExtras"));

const TOOL_INFO_CONTENT = {
  what: `Calculates Body Mass Index (BMI) from your height and weight — one general, widely-used (though imperfect) health screening measure. Accepts either metric (kg/cm) or imperial (lb/ft+in) units. For daily calorie needs specifically, use the separate Calorie Calculator tool.`,
  how: `Choose your preferred units, enter your height and weight, and your BMI and category are calculated automatically. Age, sex and waist are optional: under age 20 adult categories are hidden, the Asian cut-offs option moves the overweight and obese lines to 23 and 27.5, and a waist measurement adds a waist-to-height ratio.`,
  tip: `BMI doesn't distinguish between muscle and fat, so it can be misleading for very muscular individuals or older adults with reduced muscle mass — it's a useful general screening number, not a complete picture of health on its own.`,
};

const SEL = "border rounded px-2 py-2 w-full bg-background text-sm";
const COLORS: Record<Category, { text: string; fill: string }> = {
  Underweight: { text: "text-blue-500", fill: "#3b82f6" }, Normal: { text: "text-green-500", fill: "#22c55e" },
  Overweight: { text: "text-yellow-500", fill: "#eab308" }, Obese: { text: "text-red-500", fill: "#ef4444" },
};

function Gauge({ bmi, asian, category }: { bmi: number; asian: boolean; category: Category }) {
  const segs = gaugeSegments(asian), c = cutsFor(asian), W = 300, x = (f: number) => f * W, pos = x(gaugePos(bmi));
  const alt = `BMI gauge from 15 to 40. Cut-offs at ${c.under}, ${c.over} and ${c.obese}. Your BMI is ${bmi.toFixed(1)}, in the ${category} range.`;
  return (
    <figure className="mt-4" data-testid="bmi-gauge">
      <svg viewBox={`0 0 ${W} 46`} role="img" aria-label={alt} className="w-full max-w-xs mx-auto">
        {segs.map(s => <rect key={s.label} x={x(s.from)} y={18} width={Math.max(0, x(s.to) - x(s.from))} height={12} fill={COLORS[s.label].fill} opacity={0.85} />)}
        <polygon points={`${pos - 6},4 ${pos + 6},4 ${pos},16`} fill="currentColor" />
        <line x1={pos} y1={16} x2={pos} y2={32} stroke="currentColor" strokeWidth={2} />
        {[c.under, c.over, c.obese].map(v => <text key={v} x={x(gaugePos(v))} y={43} fontSize={8} textAnchor="middle" fill="currentColor">{v}</text>)}
      </svg>
      <figcaption className="sr-only">{alt}</figcaption>
    </figure>
  );
}

export default function BmiCalculator() {
  const [init] = useState(() => (typeof window === "undefined" ? { weight: null, height: null, units: "metric" as const } : bmiFromQuery(window.location.search)));
  const [units, setUnits] = useState<Units>(init.units);
  const [weight, setWeight] = useState<number | null>(init.weight ?? 70);
  const [height, setHeight] = useState<number | null>(init.height ?? 170);
  // Shareable link: weight, height and units only (replaceState, nothing stored).
  useEffect(() => {
    try {
      const q = bmiToQuery(weight, height, units);
      window.history.replaceState(null, "", `${window.location.pathname}${q ? `?${q}` : ""}${window.location.hash}`);
    } catch { /* ignore */ }
  }, [weight, height, units]);
  const [waist, setWaist] = useState<number | null>(null);
  const [ageText, setAgeText] = useState("");
  const [sex, setSex] = useState("");
  const [asian, setAsian] = useState(false);

  const ok = weight != null && height != null;
  const bmi = ok ? bmiOf(weight!, height!) : 0;
  // Classify on the number shown, so 18.48 (shown as 18.5) is not called Underweight.
  const shown = roundBmi(bmi);
  const age = ageStatus(parseNumber(ageText), ageText);
  const child = age.kind === "child";
  const category = classify(shown, asian);
  const color = COLORS[category].text;
  const { lo, hi } = ok ? healthyRangeKg(height!, asian) : { lo: 0, hi: 0 };
  const w = (kg: number) => (units === "metric" ? `${kg.toFixed(1)} kg` : `${(kg / 0.45359237).toFixed(0)} lb`);
  const delta = ok && !child ? changeToHealthy(weight!, height!, asian) : 0;
  const whtr = ok && waist != null ? waistToHeight(waist, height!) : null;

  const { blocked, gate } = useLogToolUsage(ok, 3000);

  return (
    <ToolContainer>
      <UnitToggle units={units} onChange={setUnits} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="space-y-4">
          <MeasureField label="Weight" kind="weight" units={units} initialMetric={init.weight ?? 70} onValue={setWeight} />
          <MeasureField label="Height" kind="height" units={units} initialMetric={init.height ?? 170} onValue={setHeight} />
          {units === "metric" && <p className="text-xs text-muted-foreground">Quick reference: 5'0" ≈ 152 cm · 5'4" ≈ 163 cm · 5'7" ≈ 170 cm · 5'8" ≈ 173 cm · 5'11" ≈ 180 cm · 6'2" ≈ 188 cm</p>}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="bmi-age">Age (optional)</Label>
              <Input id="bmi-age" inputMode="numeric" aria-label="Age" aria-invalid={age.kind === "invalid" ? true : undefined} value={ageText} onChange={(e) => setAgeText(e.target.value)} />
              {age.kind === "invalid" && <p role="alert" className="text-xs text-destructive mt-1">{age.message}</p>}
            </div>
            <div>
              <Label htmlFor="bmi-sex">Sex (optional)</Label>
              <select id="bmi-sex" className={SEL} value={sex} onChange={(e) => setSex(e.target.value)}>
                <option value="">Prefer not to say</option><option value="female">Female</option><option value="male">Male</option>
              </select>
            </div>
          </div>
          <MeasureField label="Waist (optional)" kind="length" units={units} initialMetric={80} onValue={setWaist} ariaLabel="Waist" optional />
          <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" className="mt-1" checked={asian} onChange={(e) => setAsian(e.target.checked)} />
            <span>Use Asian BMI cut-offs (WHO: 23 and 27.5)</span>
          </label>
          {sex && !child && <p className="text-xs text-muted-foreground">Sex does not change adult BMI categories; it is only used for child percentile charts.</p>}
        </div>
        <div className="p-6 rounded-xl border bg-card text-center" data-result>
          <p className="text-sm text-muted-foreground">Your BMI</p>
          {ok && !blocked ? (
            <>
              <p className={`text-4xl font-bold ${child ? "" : color}`} data-testid="bmi-value">{shown.toFixed(1)}</p>
              {child ? (
                <p className="text-sm mt-2 text-left" data-testid="bmi-child-note" role="status">{age.kind === "child" ? age.message : ""}</p>
              ) : (
                <>
                  <p className={`text-sm font-medium mt-1 ${color}`} data-testid="bmi-category">{category}{asian ? " (Asian cut-offs)" : ""}</p>
                  <Gauge bmi={shown} asian={asian} category={category} />
                  <p className="text-xs text-muted-foreground mt-3" data-testid="bmi-range">Healthy range for your height: {w(lo)} to {w(hi)}</p>
                  {delta !== 0 && (
                    <p className="text-sm mt-2" data-testid="bmi-change">
                      To reach the healthy range you would need to {delta < 0 ? "lose" : "gain"} about {units === "metric" ? `${Math.abs(delta).toFixed(1)} kg` : `${Math.round(Math.abs(delta) / 0.45359237)} lb`}.
                    </p>
                  )}
                </>
              )}
              {whtr && (
                <p className="text-sm mt-3 text-left" data-testid="bmi-whtr">
                  Waist-to-height ratio: <strong>{whtr.ratio.toFixed(2)}</strong>. {whtr.note}
                </p>
              )}
              <p className="text-xs text-muted-foreground mt-2" data-testid="bmi-disclaimer" data-no-copy>BMI is a screening number, not a diagnosis. It does not apply to children or teenagers, and it can mislead for very muscular people, older adults and during pregnancy. Talk to a doctor about your health.</p>
            </>
          ) : (
            <p className="text-4xl font-bold" data-testid="bmi-value">—</p>
          )}
        </div>
      </div>
      {ok && !blocked && !child && age.kind !== "invalid" && (
        <Suspense fallback={null}>
          <BmiExtras kg={weight!} cm={height!} imperial={units === "imperial"} gate={gate} />
        </Suspense>
      )}
      <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
